/**
 * Recent Activity Widget
 * 
 * Shows the user's most recent sessions and activities
 * with quick actions to resume.
 */

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Clock, 
  Play, 
  ChevronRight,
  Loader2
} from 'lucide-react';
import { GlassPanel } from '@/components/layout';
import { cn } from '@/lib/cn';
import { sessionApi, imageApi, documentsApi, Session, GeneratedDocumentItem } from '@/lib/api';
import { 
  AIAssistantIcon, 
  SessionsIcon, 
  LighthouseIcon 
} from '@/components/icons/CustomIcons';

interface ActivityItem {
  id: string;
  type: 'voice' | 'image' | 'document';
  title: string;
  preview: string;
  timestamp: Date;
  imageData?: string;
  mimeType?: string;
}

const typeConfig = {
  voice: {
    icon: AIAssistantIcon,
    color: 'bg-ocean-cyan/20 text-ocean-cyan',
    label: 'Voice Session',
  },
  image: {
    icon: LighthouseIcon,
    color: 'bg-ocean-pink/20 text-ocean-pink',
    label: 'Generated Image',
  },
  document: {
    icon: SessionsIcon,
    color: 'bg-ocean-purple/20 text-ocean-purple',
    label: 'Generated Document',
  },
};

function formatTimeAgo(date: Date): string {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const hours = Math.floor(diff / 3600000);
  const minutes = Math.floor(diff / 60000);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return 'Just now';
}

export function RecentActivity() {
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchActivity = async () => {
      setIsLoading(true);
      try {
        // Fetch sessions, images, and generated documents in parallel
        const [sessionsRes, imagesRes, docsRes] = await Promise.all([
          sessionApi.list(0, 5).catch(() => ({ data: [] })),
          imageApi.history(0, 5).catch(() => ({ data: [] })),
          documentsApi.history(0, 5).catch(() => ({ data: [] as GeneratedDocumentItem[] })),
        ]);

        const sessions: ActivityItem[] = sessionsRes.data.map((s: Session) => ({
          id: s.id,
          type: 'voice' as const,
          title: s.summary || 'Voice Session',
          preview: s.summary || 'Conversation with DryVox AI',
          timestamp: new Date(s.started_at),
        }));

        const images: ActivityItem[] = imagesRes.data.map((img: any) => ({
          id: `img-${img.id}`,
          type: 'image' as const,
          title: 'Generated Image',
          preview: img.prompt,
          timestamp: new Date(img.created_at),
          imageData: img.image_base64,
          mimeType: img.mime_type,
        }));

        const documents: ActivityItem[] = docsRes.data.map((d: GeneratedDocumentItem) => ({
          id: `doc-${d.id}`,
          type: 'document' as const,
          title: d.title || 'Generated Document',
          preview: `${d.document_type.toUpperCase()} — ${d.title}`,
          timestamp: new Date(d.created_at || Date.now()),
        }));

        // Merge and sort by timestamp, take top 5
        const merged = [...sessions, ...images, ...documents]
          .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
          .slice(0, 5);

        setActivities(merged);
      } catch (err) {
        console.error('Failed to fetch activity:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchActivity();
  }, []);

  if (isLoading) {
    return (
      <GlassPanel className="p-5">
        <h3 className="text-lg font-semibold text-white mb-4">Recent Activity</h3>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 text-ocean-cyan animate-spin" />
        </div>
      </GlassPanel>
    );
  }

  return (
    <GlassPanel className="p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">Recent Activity</h3>
        <Link
          to="/sessions"
          className="text-xs text-ocean-cyan hover:text-ocean-cyan/80 transition-colors"
        >
          View all →
        </Link>
      </div>

      {/* Activity List */}
      {activities.length === 0 ? (
        <div className="text-center py-8">
          <SessionsIcon className="w-10 h-10 mx-auto text-white/20 mb-3" />
          <p className="text-sm text-white/50">No recent activity</p>
          <Link
            to="/assistant"
            className="inline-flex items-center gap-2 mt-3 text-sm text-ocean-cyan hover:text-ocean-cyan/80"
          >
            Start a session <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      ) : (
        <div className="space-y-2">
          {activities.map((activity, index) => {
            const config = typeConfig[activity.type];
            const Icon = config.icon;

            return (
              <motion.div
                key={activity.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Link
                  to={activity.type === 'image' || activity.type === 'document' ? '/assistant?tab=downloads' : `/sessions?selected=${activity.id}`}
                  className="flex items-center gap-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 border border-white/5 hover:border-white/10 transition-all group"
                >
                  {/* Icon or Image Thumbnail */}
                  {activity.type === 'image' && activity.imageData ? (
                    <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        src={`data:${activity.mimeType};base64,${activity.imageData}`}
                        alt={activity.preview}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className={cn(
                      'w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0',
                      config.color
                    )}>
                      <Icon className="w-5 h-5" />
                    </div>
                  )}

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-white/40 uppercase tracking-wider">
                        {config.label}
                      </span>
                    </div>
                    <p className="text-sm text-white font-medium truncate mt-0.5 group-hover:text-ocean-cyan transition-colors">
                      {activity.preview.length > 50 
                        ? activity.preview.substring(0, 50) + '...' 
                        : activity.preview}
                    </p>
                  </div>

                  {/* Time & Action */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="flex items-center gap-1 text-xs text-white/30">
                      <Clock className="w-3 h-3" />
                      {formatTimeAgo(activity.timestamp)}
                    </span>
                    <div className="p-1.5 rounded-md bg-ocean-cyan/20 text-ocean-cyan opacity-0 group-hover:opacity-100 transition-opacity">
                      <Play className="w-3 h-3" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      )}
    </GlassPanel>
  );
}

export default RecentActivity;

